import {
  useQuery,
  useMutation,
  // useQueryClient,
  type QueryKey,
  useQueryClient
} from "@tanstack/react-query";
import { useAttributesIds, useAttributesQuery } from "./general";
import { ERRORS } from "../consts";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import type { RunsticResponse, UserGoals, UserGoalsConfigs } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useLearnerProgressQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerProgress/${corporateId}/${userId}?initiativeId=${initiativeId}`;

  const queryOptions = {
    enabled:
      Boolean(
        enabled &&
        userId &&
        accessToken &&
        sessionToken &&
        corporateId &&
        !isLoading &&
        initiativeId &&
        smartConfiguration?.progressWidget
      ) && !isSeedingSession,
    queryFn: async () => {
      try {
        const res = await fetcher(`${basePath}${uri}`, {
          headers: {
            Authorization: accessToken,
            "x-ada-session-token": sessionToken
          }
        });

        return await res.json();
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
        }
        throw error;
      }
    },
    queryKey: ["userProgress", userId, corporateId, sessionToken],
    refetchOnMount: false, // Cambiato da true per evitare chiamate eccessive
    retry: (failureCount: number, error: unknown) => {
      // Logica di retry personalizzata per evitare cicli infiniti
      if (failureCount >= 2) {
        return false; // Ferma i retry dopo 2 tentativi
      }

      // Non fare retry per errori di autorizzazione
      if (error === ERRORS.UNAUTHORIZED) {
        return false;
      }

      // Controllo per errori con status HTTP
      if (typeof error === "object" && error !== null && "status" in error) {
        const status = (error as { status: number }).status;
        // Non fare retry per la maggior parte degli errori client
        if (status >= 400 && status < 500) {
          return false;
        }
      }

      // Controllo per altri tipi di errore che non dovrebbero essere riprovati
      if (typeof error === "string" && error.includes("Failed to fetch")) {
        // Errori di rete - limita i retry
        if (failureCount >= 1) {
          return false;
        }
      }

      return true;
    },
    retryDelay: (attemptIndex: number) => Math.min(2000 * Math.pow(2, attemptIndex), 10000), // Delay più conservativo
    staleTime: 3 * 60 * 1000
  } as const;

  return useQuery<RunsticResponse>(queryOptions);
}

export function useGetLearnerGoalsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  return useQuery<UserGoals>({
    enabled:
      Boolean(
        enabled &&
        userId &&
        accessToken &&
        sessionToken &&
        corporateId &&
        smartConfiguration?.progressWidget
      ) && !isSeedingSession,
    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "Authorization": accessToken,
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      })
      .then((res) => {
        return res.json();
      })
      .then((data: {data: UserGoals}) => {
        return data.data;
      })
      .catch((error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }),
    queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoals"],
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });
}

export function useGetLearnerGoalsConfigsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/configs/${corporateId}`;

  return useQuery<
      unknown,
      unknown,
      UserGoalsConfigs,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
        && !isLoading
        && smartConfiguration?.progressWidget
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: UserGoalsConfigs) => {
          return data;
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoalsConfigs"],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

export function usePutLearnerGoalsQuery(plan: string) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  const queryClient = useQueryClient();

  return useMutation({

    mutationFn: () => fetcher(
      `${basePath}${uri}`,
      {
        body: JSON.stringify({
          plan: plan.toUpperCase()
        }),
        headers:{
          "authorization": accessToken,
          "content-type":"application/json",
          "x-ada-session-token": sessionToken
        },
        method: "PUT"
      })
      .catch(async (error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw await error;
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learnerGoals", userId, corporateId, sessionToken] });
    }
  });
}


export function useUserProgressWithAttributes({ enabled = true }: { enabled?: boolean } = {}): {
  data: RunsticResponse | null;
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isLoadingError: boolean;
  translatedAttribute: boolean;
  [key: string]: unknown;
} {
  // Utilizzo direttamente useLearnerProgressQuery per ottenere i dati base
  const progressQuery = useLearnerProgressQuery({ enabled });

  // Ottengo gli attributi processati separatamente (stessa fonte di useUserAttributes/AttributeTabs)
  const { data: attributeNames } = useAttributesQuery();
  const { data: attributesIds } = useAttributesIds();

  // Se la query è in errore, propago lo stato di errore
  if (progressQuery.isError) {
    return {
      ...progressQuery,
      data: null,
      translatedAttribute: false
    };
  }

  // Se la query è in stato di loading o non ha dati, ritorno la query senza modifiche
  if (progressQuery.isLoading || !progressQuery.data) {
    return {
      ...progressQuery,
      data: null,
      translatedAttribute: false
    };
  }

  // Processo gli attributi con i nomi corretti
  const rawAttributes = (progressQuery.data as RunsticResponse).attributes?.map(progressAttr => {
    // Trovo il nome dell'attributo corrispondente
    const attributeNameData = attributeNames?.find(nameAttr =>
      nameAttr.attributeId.toString() === progressAttr.attributeId.toString()
    );

    return {
      attributeId: progressAttr.attributeId,
      attributeName: attributeNameData?.attribute || progressAttr.attributeName,
      completedLos: progressAttr.completedLos || 0,
      los: progressAttr.los || 0
    };
  }) || [];

  // attributi assegnati all'utente e con forYou TRUE
  const userAttributeIds = new Set((attributesIds ?? []).map(id => id.toString()));
  const enhancedAttributes = rawAttributes.filter(attr => {
    if (!userAttributeIds.has(attr.attributeId.toString())) return false;
    const attributeNameData = attributeNames?.find(nameAttr =>
      nameAttr.attributeId.toString() === attr.attributeId.toString()
    );
    return attributeNameData?.forYou === true;
  });

  const firstAdminAttributeId = attributeNames?.[0]?.attributeId;
  const translatedAttribute = firstAdminAttributeId != null &&
    enhancedAttributes.some(a => a.attributeId.toString() === firstAdminAttributeId.toString());

  let orderedAttributes = enhancedAttributes;
  if (attributesIds && attributesIds.length > 0) {
    const sortedByUserOrder = attributesIds
      .map(id => enhancedAttributes.find(a => a.attributeId.toString() === id.toString()))
      .filter((a): a is NonNullable<typeof a> => a != null);
    const notInIds = enhancedAttributes.filter(a =>
      !attributesIds.some(id => id.toString() === a.attributeId.toString())
    );
    orderedAttributes = [...sortedByUserOrder, ...notInIds];
  }
  if (translatedAttribute && firstAdminAttributeId != null) {
    const firstAttr = orderedAttributes.find(a => a.attributeId.toString() === firstAdminAttributeId.toString())!;
    const rest = orderedAttributes.filter(a => a.attributeId.toString() !== firstAdminAttributeId.toString());
    orderedAttributes = [firstAttr, ...rest];
  }

  const enhancedData: RunsticResponse = {
    ...(progressQuery.data as RunsticResponse),
    attributes: orderedAttributes
  };

  return {
    ...progressQuery,
    data: enhancedData,
    translatedAttribute
  };
}

